/**
 * Reactive-store factory — the single seam over this app's out-of-Apollo-cache
 * client state (panel toggles, UI prefs, in-flight flags, …). Consumers use the
 * returned [use, get, set] tuple only; the makeVar backing is private to this
 * file, so swapping in a different store library (redux/zustand/…) later is one
 * edit here rather than a change at every call site. See CLAUDE.md → "Client
 * state routes through the store factory".
 */
import { makeVar, useReactiveVar } from '@apollo/client';

// `use` is a hook (it calls useReactiveVar) — obey the Rules of Hooks at every
// call site and name each re-export `useX`. `get`/`set` are plain read/write.
export type ReactiveStore<T> = [use: () => T, get: () => T, set: (value: T) => void];

export const createStore = <T>(initial: T): ReactiveStore<T> => {
  const state = makeVar<T>(initial);
  const useValue = () => useReactiveVar(state);
  // set returns void (makeVar's setter returns the value) so `() => set(x)`
  // callbacks — onClick, act() — don't leak a truthy return.
  return [useValue, () => state(), (value: T) => { state(value); }];
};

type StorageLike = Pick<Storage, 'getItem' | 'setItem' | 'removeItem'>;

interface PersistedStoreOptions {
  storage?: 'local' | 'session' | StorageLike;
}

// Resolve defensively — reading window storage can throw (SSR, privacy modes);
// on failure the store degrades to in-memory only rather than crashing at load.
const resolveStorage = (kind: 'local' | 'session' | StorageLike): StorageLike | null => {
  try {
    if (kind === 'local') return typeof window !== 'undefined' ? window.localStorage : null;
    if (kind === 'session') return typeof window !== 'undefined' ? window.sessionStorage : null;
    return kind;
  } catch {
    return null;
  }
};

// Persisted variant: seeds from storage on creation and writes through (JSON) on
// every set. A missing/corrupt/unavailable slot falls back to `initial`.
export const createPersistedStore = <T>(
  key: string,
  initial: T,
  { storage = 'local' }: PersistedStoreOptions = {}
): ReactiveStore<T> => {
  const store = resolveStorage(storage);

  const readInitial = (): T => {
    if (!store) return initial;
    try {
      const raw = store.getItem(key);
      return raw === null ? initial : (JSON.parse(raw) as T);
    } catch {
      return initial;
    }
  };

  const [useValue, get, setValue] = createStore<T>(readInitial());

  const set = (value: T) => {
    setValue(value);
    if (!store) return;
    try {
      store.setItem(key, JSON.stringify(value));
    } catch {
      console.warn(`createPersistedStore: could not persist "${key}"; keeping in-memory value only`);
    }
  };

  return [useValue, get, set];
};
